import { createFileRoute } from '@tanstack/react-router'
import { INVESTOR_NAV_LABEL } from '@workspace/seedforge-ui/lib/investor-nav'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@workspace/ui/components/tabs'
import { Copy, MessageSquare } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { AssistantGate } from '../../../../features/investor-assistant/components/assistant-gate'
import {
  AssistantGuideCard,
  SERVER_URL,
} from '../../../../features/investor-assistant/components/connect/assistant-guide'
import {
  ASSISTANT_GUIDES,
  DEFAULT_ASSISTANT_ID,
  EXAMPLE_QUESTIONS,
  OTHER_ASSISTANTS_ID,
} from '../../../../features/investor-assistant/components/connect/assistant-guides'
import { ConnectedPanel } from '../../../../features/investor-assistant/components/connect/connected-panel'
import { OtherAssistantsCard } from '../../../../features/investor-assistant/components/connect/other-assistants-card'
import { useConnectionsQuery } from '../../../../features/investor-assistant/hooks/use-investor-assistant'
import { useCopyToClipboard } from '../../../../hooks/use-copy-to-clipboard'
import { trackEvent } from '../../../../lib/gtag'

export const Route = createFileRoute('/app/investor/$id/connect-ai')({
  component: ConnectAiPage,
})

const LAST_TAB_KEY = 'seedforge-connect-ai-tab'
const POLL_MS = 5_000
const POLL_FOR_MS = 10 * 60 * 1000

/** Until the connections load, only the tabs that are always there. */
const ALWAYS_TABS = ['claude', 'chatgpt', OTHER_ASSISTANTS_ID]

function readLastTab() {
  return localStorage.getItem(LAST_TAB_KEY) ?? DEFAULT_ASSISTANT_ID
}

function CheckRunningNote() {
  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4">
      <p className="text-sm font-medium text-foreground">You can connect now.</p>
      <p className="mt-0.5 text-sm text-muted-foreground">
        Your AI can search while your account check runs and open full records when the check is
        complete.
      </p>
    </div>
  )
}

function StatusPill({ connected }: { connected: boolean }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium ${
        connected ? 'bg-emerald-500/10 text-emerald-500' : 'bg-muted text-muted-foreground'
      }`}
    >
      <span className="size-1.5 rounded-full bg-current" />
      {connected ? 'Connected' : 'Not connected'}
    </span>
  )
}

function ServerAddressCard({
  asSecondary,
  waiting,
  gaveUp,
  onCopied,
  onCheckAgain,
}: {
  asSecondary: boolean
  waiting: boolean
  gaveUp: boolean
  onCopied: () => void
  onCheckAgain: () => void
}) {
  const { copied, copy } = useCopyToClipboard()
  const Button = asSecondary ? SecondaryButton : CtaButton
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Server address
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <code className="min-w-0 flex-1 overflow-x-auto rounded-lg border border-border bg-input/50 px-4 py-3 text-sm text-foreground">
          {SERVER_URL}
        </code>
        <Button
          icon={<Copy className="size-4" />}
          onClick={() => {
            copy(SERVER_URL)
            trackEvent('assistant_setup_copied', { target: 'server_address' })
            onCopied()
          }}
        >
          {copied ? 'Copied' : 'Copy address'}
        </Button>
      </div>
      <p className="mt-3 text-sm text-muted-foreground">
        Paste it where your assistant asks for a custom connector or MCP server, then sign in with
        your SeedForge account.
      </p>
      {waiting && (
        <p className="mt-2 text-sm text-muted-foreground">
          Waiting for your assistant to connect. This page updates by itself.
        </p>
      )}
      {gaveUp && <p className="mt-2 text-sm text-muted-foreground">Nothing connected yet.</p>}
      {gaveUp && (
        <SecondaryButton size="sm" className="mt-3" onClick={onCheckAgain}>
          Check again
        </SecondaryButton>
      )}
    </div>
  )
}

function ConnectAiPage() {
  const [tab, setTab] = useState(readLastTab)
  const [waitingSince, setWaitingSince] = useState<number | null>(null)
  const [gaveUp, setGaveUp] = useState(false)
  const connections = useConnectionsQuery(waitingSince ? POLL_MS : undefined)
  const items = connections.data?.data.items ?? []
  const [firstConnection, ...otherConnections] = items
  const connected = firstConnection !== undefined
  const checkRunning = connections.data ? !connections.data.data.checkPassed : false
  // Gemini, Copilot, Perplexity and Le Chat appear once their "Works with" test has passed.
  const shownTabs = connections.data?.data.assistantTabs ?? ALWAYS_TABS
  const guides = ASSISTANT_GUIDES.filter((g) => shownTabs.includes(g.id as never))
  const activeTab =
    tab === OTHER_ASSISTANTS_ID || guides.some((g) => g.id === tab) ? tab : DEFAULT_ASSISTANT_ID
  const activeGuide = guides.find((g) => g.id === activeTab)
  const trackedConnected = useRef(false)

  // Stop waiting after ten minutes, or as soon as something connects.
  useEffect(() => {
    if (!waitingSince) return
    const left = POLL_FOR_MS - (Date.now() - waitingSince)
    const timer = setTimeout(
      () => {
        setWaitingSince(null)
        setGaveUp(true)
      },
      Math.max(left, 0)
    )
    return () => clearTimeout(timer)
  }, [waitingSince])

  useEffect(() => {
    if (!firstConnection) return
    if (waitingSince && !trackedConnected.current) {
      trackedConnected.current = true
      trackEvent('assistant_connected', { assistant: firstConnection.name })
    }
    setWaitingSince(null)
    setGaveUp(false)
  }, [firstConnection, waitingSince])

  const startWaiting = () => {
    setGaveUp(false)
    setWaitingSince(Date.now())
  }

  const setup = (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-foreground">Set it up in</h2>
      <Tabs
        value={activeTab}
        onValueChange={(next) => {
          setTab(next)
          localStorage.setItem(LAST_TAB_KEY, next)
          trackEvent('assistant_tab_selected', { assistant: next })
        }}
      >
        <TabsList className="h-auto w-full flex-nowrap justify-start gap-1.5 overflow-x-auto bg-transparent p-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {[...guides, { id: OTHER_ASSISTANTS_ID, label: 'Other assistants' }].map((item) => (
            <TabsTrigger
              key={item.id}
              value={item.id}
              className="shrink-0 rounded-full border border-border px-2.5 py-1 text-xs data-[state=active]:border-transparent data-[state=active]:bg-muted"
            >
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {guides.map((guide) => (
          <TabsContent key={guide.id} value={guide.id} className="mt-3">
            <AssistantGuideCard
              guide={guide}
              chatgptNotify={connections.data?.data.chatgptNotify ?? false}
              onOpened={startWaiting}
            />
          </TabsContent>
        ))}
        <TabsContent value={OTHER_ASSISTANTS_ID} className="mt-3">
          <OtherAssistantsCard />
        </TabsContent>
      </Tabs>
    </section>
  )

  return (
    <AssistantGate activeNav={INVESTOR_NAV_LABEL.connectAi}>
      <div className="mx-auto max-w-3xl space-y-8 py-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Connect your AI
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {connected
                ? 'Ask your assistant about any startup on SeedForge. It answers from what founders published, with dates, and follows a startup only after your yes.'
                : 'Ask the AI assistant you already use about startups on SeedForge. It answers from what founders published, with dates, and follows a startup only after your yes.'}
            </p>
          </div>
          {!connections.isPending && <StatusPill connected={connected} />}
        </div>

        {checkRunning && <CheckRunningNote />}

        {connected ? (
          <>
            <ConnectedPanel
              connections={[firstConnection, ...otherConnections]}
              setupGuide={setup}
              checkRunning={checkRunning}
            />
          </>
        ) : (
          <>
            <ServerAddressCard
              asSecondary={activeGuide?.adminRequest === true || activeGuide?.setupDetails === true}
              waiting={waitingSince !== null}
              gaveUp={gaveUp}
              onCopied={startWaiting}
              onCheckAgain={() => {
                startWaiting()
                void connections.refetch()
              }}
            />
            {setup}
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-foreground">Then ask in your own words</h2>
              <ul className="space-y-2">
                {(checkRunning ? EXAMPLE_QUESTIONS.slice(0, 1) : EXAMPLE_QUESTIONS).map(
                  (question) => (
                    <li key={question} className="flex items-start gap-3 text-sm text-foreground">
                      <MessageSquare className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                      <span>“{question}”</span>
                    </li>
                  )
                )}
              </ul>
            </section>
          </>
        )}
      </div>
    </AssistantGate>
  )
}
